import React, { useEffect, useState, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CheckoutPage.css";
import CartSubtitle from "./Components/CartSubtitle/CartSubtitle";
import CheckoutFooter from "./Components/CheckoutFooter/CheckoutFooter";
import UniversalCheckoutHeader from "../../UniversalComponents/CheckoutHeader/CheckoutHeader";
import {
  FinancialData,
  ItemDetailsData,
  RecommendedItem,
} from "../../Utils/DefinitionsInterfaces";
import { merchantLogoString } from "../../Utils/Constants";
import {
  doNFOTransactionResponse,
  fetchRecommendedItems,
  nfoGetAvailableBooks,
  verifyNFOTransaction,
  Book,
  verifyPaymentStatusRazorpay,
  getCouponDiscount,
  CouponCodeResponse,
} from "../../Utils/CheckoutAPIs/CheckoutAPIs";
import { CircularProgress } from "@mui/material";
import CheckoutPaymentFooter from "./Components/CheckoutPaymentFooter/CheckoutPaymentFooter";
import PaymentFailedModal from "./Components/PaymentFailed/PaymentFailedModal";
import * as Sentry from "@sentry/react";
import SummaryModal from "./Components/Modal/SummaryModal";
import { CartContext } from "../../App";
import { trackData } from "../../Utils/attribution/CheckoutAttribution";
import ReactGA from "react-ga4";
import { experimentTracker } from "../../Utils/ExperimentTracker";
import {
  getRemoteConfig,
  getValue,
  fetchAndActivate,
} from "firebase/remote-config";
import { app, configuration } from "../../configuration";
import usePricing from "../../Utils/hooks/usePricing";

interface UserDetails {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  address: string;
  flatNo: string;
  city: string;
  state: string;
  pincode: string;
  paymentType: "prepaid" | "cod";
  schoolName: string;
  grade: number;
}

interface RemoteConfigData {
  pfhSuccessBanner: {
    enabled: boolean;
    title: string;
    subtitle: string;
    image_url: string;
    cta_text: string;
    cta_url: string;
    background_color: string;
    text_color: string;
    valid_from: string;
    valid_until: string;
  };
}

const CheckoutPage = ({ history }: any) => {
  console.log("history", history);
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const orderId = urlParams.get("order_id");
  const [paymentFailed, setPaymentFailed] = useState(false);
  const itemIds = sessionStorage.getItem("item_ids");
  const bundleLandingParam = urlParams.get("bundleLanding");
  const sessionBundleLanding = sessionStorage.getItem("bundleLanding");
  const isBundleLandingFlow = useMemo(() => {
    const flag = bundleLandingParam ?? sessionBundleLanding ?? "";
    const normalized = flag.toLowerCase();
    return normalized === "true" || flag === "1";
  }, [bundleLandingParam, sessionBundleLanding]);
  const itemIdsUrlParams = urlParams.get("item_ids");

  const {
    setUserDetails,
    backendSchool,
    setCheckOutItems,
    checkOutItems,
    userDetails,
    setNfoSelected,
    setSchoolID,
    nfoSelected,
    bundleInfo,
    setBundleInfo,
  } = useContext(CartContext);
  const nfoSelectedSession = sessionStorage.getItem("nfoSelected");
  const nfoSelectedBoolean = nfoSelectedSession === "true";

  // Bundle pricing detection
  const itemIdsParam = urlParams.get("item_ids");

  useEffect(() => {
    // Check for bundle pricing trigger
    if (itemIdsParam && (itemIdsParam === "1" || itemIdsParam === "2")) {
      setBundleInfo({
        isBundle: true,
        primaryProductId: parseInt(itemIdsParam),
      });
      console.log("Bundle pricing enabled for primary product:", itemIdsParam);
    }
  }, [itemIdsParam, setBundleInfo]);

  useEffect(() => {
    if (itemIdsUrlParams) {
      sessionStorage.setItem("item_ids", itemIdsUrlParams);
      console.log("itemIdsUrlParams", itemIdsUrlParams);
    } else if (itemIds && itemIds.length > 0) {
      sessionStorage.setItem("item_ids", itemIds);
      console.log("itemIds", itemIds);
    } else {
      const searchParams = new URLSearchParams({ item_ids: "1" }).toString();
      const newUrl = `/CheckoutPage/?${searchParams}`;
      console.log("newUrl", newUrl);
      navigate(newUrl, { replace: true });
      window.location.reload();
    }
  }, [navigate, itemIdsUrlParams, itemIds]);

  useEffect(() => {
    if (bundleLandingParam) {
      sessionStorage.setItem("bundleLanding", bundleLandingParam);
    }
  }, [bundleLandingParam]);
  const [verifyPaymentProgress, setVerifyPaymentProgress] =
    useState<boolean>(false);

  // Coupon State Management
  interface CouponState {
    code: string;
    isApplied: boolean;
    isLoading: boolean;
    discountAmount: number;
    originalAmount: number;
    finalAmount: number;
    error: string;
  }

  const [couponState, setCouponState] = useState<CouponState>({
    code: "",
    isApplied: false,
    isLoading: false,
    discountAmount: 0,
    originalAmount: 0,
    finalAmount: 0,
    error: "",
  });

  const [couponInput, setCouponInput] = useState("");
  const [lastAttemptedCoupon, setLastAttemptedCoupon] = useState("");

  // Use pricing hook - default to "prepaid" for coupon calculations
  const {
    calculateCurrentAmount,
    getBundleParams,
    getAppliedCouponCode,
    forceUpdate,
  } = usePricing("prepaid");

  // Check if coupon is enabled (show for both prepaid and COD since user hasn't selected yet)
  const isCouponEnabled = (): boolean => {
    if (isBundleLandingFlow) return false;
    if (!configuration.coupon.enabled) return false;
    return (
      configuration.coupon.enableForPrepaid || configuration.coupon.enableForCOD
    );
  };

  // Apply coupon handler - accepts optional code parameter for direct application
  const handleApplyCoupon = async (codeToApply?: string) => {
    const code = codeToApply || couponInput.trim();

    if (!code) {
      setCouponState((prev) => ({
        ...prev,
        error: "Please enter a coupon code",
      }));
      return;
    }

    const upperCode = code.toUpperCase();
    setLastAttemptedCoupon(upperCode);

    // Update coupon input if code was passed directly
    if (codeToApply) {
      setCouponInput(upperCode);
    }

    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = calculateCurrentAmount();
      const response: CouponCodeResponse = await getCouponDiscount(
        upperCode,
        currentAmount
      );

      console.log("Coupon API Response:", response);

      if (response.status === "Success" || response.status === "success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );

        // Force update to refresh pricing calculations
        forceUpdate();

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("couponUpdated"));

        // Track ReactGA event for successful coupon application
        ReactGA.event({
          category: "Apply_Coupon_Success",
          action: "Apply_Coupon_Success",
          label: response.data.coupon_code,
        });
      } else {
        setCouponState((prev) => ({
          ...prev,
          isLoading: false,
          error: response.message || "Invalid coupon code",
        }));

        // Track ReactGA event for failed coupon application
        ReactGA.event({
          category: "Coupon_Interaction",
          action: "Apply_Coupon_Failed",
          label: upperCode,
        });
      }
    } catch (error: any) {
      console.error("Coupon API Error:", error);

      let errorMessage = "Invalid coupon code";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 404) {
        errorMessage = "Coupon service is currently unavailable";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (!error.response) {
        errorMessage = "Network error. Please check your connection.";
      }

      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      // Track ReactGA event for coupon application error
      ReactGA.event({
        category: "Apply_Coupon_Error",
        action: "Apply_Coupon_Error",
        label: upperCode,
      });
    }
  };

  // Remove coupon handler
  const handleRemoveCoupon = () => {
    setCouponState({
      code: "",
      isApplied: false,
      isLoading: false,
      discountAmount: 0,
      originalAmount: 0,
      finalAmount: 0,
      error: "",
    });
    setCouponInput("");
    setLastAttemptedCoupon("");

    // Clear session storage
    sessionStorage.removeItem("appliedCouponCode");
    sessionStorage.removeItem("couponDiscountAmount");
    sessionStorage.removeItem("originalAmount");

    // Force update to refresh pricing calculations
    forceUpdate();

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event("couponUpdated"));
  };

  // Restore coupon from session storage on component mount
  useEffect(() => {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    const couponDiscountAmount = sessionStorage.getItem("couponDiscountAmount");
    const originalAmount = sessionStorage.getItem("originalAmount");

    if (appliedCouponCode && couponDiscountAmount && originalAmount) {
      const discountAmount = parseFloat(couponDiscountAmount);
      const original = parseFloat(originalAmount);

      setCouponState({
        code: appliedCouponCode,
        isApplied: true,
        isLoading: false,
        discountAmount: discountAmount,
        originalAmount: original,
        finalAmount: original - discountAmount,
        error: "",
      });
      setCouponInput(appliedCouponCode);
      setLastAttemptedCoupon(appliedCouponCode);
    }
  }, []);

  // Clear coupon error when user modifies input from last attempted coupon
  useEffect(() => {
    if (
      couponState.error &&
      couponInput.trim().toUpperCase() !== lastAttemptedCoupon
    ) {
      setCouponState((prev) => ({ ...prev, error: "" }));
    }
  }, [couponInput, couponState.error, lastAttemptedCoupon]);

  const areUserDetailsFilled = (userDetails: UserDetails): boolean => {
    // Basic fields that are always required
    const basicFieldsFilled =
      userDetails.firstName !== "" &&
      userDetails.lastName !== "" &&
      userDetails.email !== "" &&
      userDetails.mobileNumber !== "" &&
      userDetails.flatNo !== "" &&
      userDetails.address !== "" &&
      userDetails.city !== "" &&
      userDetails.state !== "" &&
      userDetails.pincode !== "";

    // If NFO is selected, also check school details
    if (nfoSelected || nfoSelectedBoolean) {
      return (
        basicFieldsFilled &&
        userDetails.schoolName !== "" &&
        userDetails.grade > 0
      );
    }

    return basicFieldsFilled;
  };

  useEffect(() => {
    if (areUserDetailsFilled(userDetails)) {
      trackData("user_details_filled");
      ReactGA.event({
        category: "user_details_filled",
        action: "user_details_filled",
        label: "user_details_filled",
      });

      // Experiment funnel step 2
      experimentTracker.trackPageView(
        "/funnel/step2_details_filled",
        "Details Filled"
      );
    }
  }, [userDetails]);

  const nfoCheckoutAccessToken = urlParams.get("token");
  if (nfoCheckoutAccessToken) {
    localStorage.setItem("nfoCheckoutAccessToken", nfoCheckoutAccessToken);
  }

  const [recommendedItems, setRecommendedItems] = useState<RecommendedItem[]>(
    []
  );

  const [allItemDetailsDB, setAllItemDetailsDB] = useState<ItemDetailsData[]>(
    []
  );
  const [defaultItems, setDefaultItems] = useState<FinancialData[]>([]);
  const [checkoutItemIds, setCheckoutItemIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  const verifyPayment = async (orderId: string) => {
    try {
      const errorStatuses = [
        "payment_processing_error",
        "user_cancelled_error",
        "acquirer_processing_error",
        "risk_validation_error",
      ];

      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyNFOTransaction(orderId);
          console.log("This is new verify response after retry =", newResponse);
          //@ts-ignore
          const newPaymentStatus = newResponse.data.data.paymentStatus;
          if (newPaymentStatus === "success") {
            console.log("Payment Successfully completed");
            return true;
          } else if (newPaymentStatus === "retry") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          } else if (errorStatuses.includes(newPaymentStatus)) {
            console.log("Payment failed");
            return false;
          } else {
            return false;
          }
        }
        return false;
      };

      const response = await verifyNFOTransaction(orderId);
      console.log("This is the verify response", response);
      //@ts-ignore
      const paymentStatus = response.data.data.paymentStatus;
      if (paymentStatus === "success") {
        console.log("Payment Successful");
        return true;
      } else if (paymentStatus === "retry") {
        console.log("Retry payment");
        return await retryPayment(3);
      } else if (errorStatuses.includes(paymentStatus)) {
        console.log("Payment failed");
        return false;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const verifyPaymentRazorpay = async (orderId: string) => {
    try {
      const retryPayment = async (
        remainingRetries: number
      ): Promise<boolean> => {
        while (remainingRetries > 0) {
          const newResponse = await verifyPaymentStatusRazorpay(orderId);
          console.log("This is new verify response after retry =", newResponse);

          const newPaymentStatus = newResponse.data.status;
          if (newPaymentStatus === "paid") {
            console.log("Payment Successful");
            return true;
          } else if (newPaymentStatus === "created") {
            remainingRetries--;
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          } else if (newPaymentStatus === "attempted") {
            console.log("Payment failed");
            return false;
          } else {
            return false;
          }
        }
        return false;
      };

      const response = await verifyPaymentStatusRazorpay(orderId);
      console.log("This is the verify response", response);
      //@ts-ignore
      const paymentStatus = response.data.status;
      if (paymentStatus === "paid") {
        console.log("Payment Successful");
        return true;
      } else if (paymentStatus === "created") {
        console.log("Retry payment");
        return await retryPayment(3);
      } else if (paymentStatus === "attempted") {
        console.log("Payment failed");
        return false;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  // const populateDefaultItems = (defaultItem: FinancialData) => {
  //   let presDefaultItems: FinancialData[] = [];
  //   setDefaultItems([defaultItem]);
  // };

  // const getAndSetAllItemsDB = async (
  //   schoolCode: string
  // ): Promise<ItemDetailsData[]> => {
  //   let tempAllItems = await fetchRecommendedItems(schoolCode);
  //   let allItems: ItemDetailsData[] = [];
  //   populateDefaultItems(tempAllItems.default);
  //   tempAllItems.recommendedItems.map((item) => {
  //     if (item.selling_price !== 0) {
  //       allItems.push(item);
  //     }
  //   });
  //   setAllItemDetailsDB(allItems);
  //   return allItems;
  // };

  const populateFullCart = (allItems: Book[]) => {
    const itemIds = sessionStorage.getItem("item_ids");
    const itemIdsArray = itemIds ? itemIds.split("-").map(Number) : [];

    const filteredItems = allItems.filter((item) =>
      itemIdsArray.includes(item.id)
    );
    console.log("filteredItems", filteredItems);

    const filteredRecommendedItems = allItems.filter(
      (item) => !itemIdsArray.includes(item.id)
    );
    console.log("filteredRecommendedItems", filteredRecommendedItems);

    // Check if NFO registration (item ID 3) is in the cart
    const hasNfoRegistration = itemIdsArray.includes(3);
    if (hasNfoRegistration) {
      setNfoSelected(true);
      sessionStorage.setItem("nfoSelected", "true");
      console.log(
        "NFO registration detected in cart, setting nfoSelected to true"
      );
    }

    setCheckOutItems(filteredItems as ItemDetailsData[]);
    setRecommendedItems(filteredRecommendedItems as RecommendedItem[]);
    setAllItemDetailsDB(allItems as ItemDetailsData[]);
  };

  const handleRemoveItem = (itemId: number) => {
    if (itemId === 3) {
      setNfoSelected(false);
      sessionStorage.removeItem("nfoSelected");
      ReactGA.event({
        category: "nfo_registration_removed",
        action: "nfo_registration_removed",
        label: "nfo_registration_removed",
      });
    }
    setCheckoutItemIds((prevItems) =>
      prevItems.filter((currId) => currId !== itemId)
    );
    setCheckOutItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.id !== itemId);

      // Update sessionStorage with the new list of item IDs
      const updatedItemIds = updatedItems.map((item) => item.id);
      const itemIdsString = updatedItemIds.join("-");
      sessionStorage.setItem("item_ids", itemIdsString);

      return updatedItems;
    });

    let tempRecoItems: ItemDetailsData[] = [];
    let foundItem = false;
    for (let i = 0; !foundItem && i < allItemDetailsDB.length; i++) {
      if (allItemDetailsDB[i].id === itemId) {
        tempRecoItems.push(allItemDetailsDB[i]);
        foundItem = true;
      }
    }
    if (tempRecoItems.length > 0)
      setRecommendedItems((prevItems) => [...prevItems, tempRecoItems[0]]);
  };

  const paymentCallbackFunction = async () => {
    console.log({ orderId });
    console.log("This is called");
    if (!orderId) return;

    const pg = sessionStorage.getItem("pg");
    const itemIds = sessionStorage.getItem("item_ids");

    console.log("Order id =", orderId);
    setVerifyPaymentProgress(true);

    const navigateToSuccess = () => {
      setVerifyPaymentProgress(false);
      navigate(
        `/PaymentSuccessful/?order_id=${orderId}&${
          itemIds ? `item_ids=${itemIds}` : ""
        }`
      );
    };

    const verifyAndNavigate = async (retries: number, delay: number) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        const orderStatus =
          pg === "razorpay"
            ? await verifyPaymentRazorpay(orderId)
            : await verifyPayment(orderId);
        console.log(`Order status after attempt ${attempt} =`, orderStatus);

        if (orderStatus) {
          navigateToSuccess();
          return;
        }

        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    };

    await verifyAndNavigate(3, 2000);

    // Final verification with external API if all retries failed
    try {
      const orderStatus =
        pg === "razorpay"
          ? await verifyPaymentRazorpay(orderId)
          : await verifyPayment(orderId);

      if (orderStatus) {
        navigateToSuccess();
        return;
      } else {
        setPaymentFailed(true);
        console.log("Final verification failed - Other Issue");
      }
    } catch (error) {
      console.log("Error during final API verification", error);
    } finally {
      setVerifyPaymentProgress(false);
    }
  };

  const handleAddItemFromRecommended = (itemId: number) => {
    setCheckoutItemIds((prevIds) => [...prevIds, itemId]);
    setRecommendedItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );

    const itemToAdd = allItemDetailsDB.find((item) => item.id === itemId);
    if (itemToAdd) {
      if (itemToAdd.id === 3) {
        setNfoSelected(true);
        sessionStorage.setItem("nfoSelected", "true");
        ReactGA.event({
          category: "nfo_registration_added",
          action: "nfo_registration_added",
          label: "nfo_registration_added",
        });
      }

      // For items with id === 3, modify the pricing to use selling_price as discountedBundlePrice
      const modifiedItem =
        itemToAdd.id === 3
          ? { ...itemToAdd, discountedBundlePrice: itemToAdd.selling_price }
          : itemToAdd;

      setCheckOutItems((prevItems) => {
        const updatedItems = [...prevItems, modifiedItem];
        // Add sessionStorage setItem logic
        const updatedItemIds = updatedItems.map((item) => item.id);
        const itemIdsString = updatedItemIds.join("-");
        sessionStorage.setItem("item_ids", itemIdsString);

        return updatedItems;
      });
    }
  };

  useEffect(() => {
    setLoading(true);

    const initializeItems = async () => {
      try {
        const allItems = await nfoGetAvailableBooks();
        setSchoolID(allItems.data.find((item) => item.id === 3)?.schoolID ?? 0);
        populateFullCart(allItems.data);
        paymentCallbackFunction();
      } catch (err) {
        console.error("Error fetching items:", err);
      } finally {
        setLoading(false);
      }
    };

    initializeItems();
  }, []);

  const handlePaymentClickNFO = async (data?: doNFOTransactionResponse) => {
    console.log("this is the data", data);
    if (backendSchool.isInternational) {
      setLoading(true);
      navigate("/PaymentPage");
    } else {
      const sellingPrice = defaultItems[0]?.selling_price;
      sessionStorage.setItem("transactionAmount", sellingPrice?.toString());
      console.log("this is the data", data);
      let itemIdsString: string = "";
      for (let i = 0; i < checkOutItems.length; i++) {
        itemIdsString += checkOutItems[i].id;
        if (i + 1 !== checkOutItems.length) {
          itemIdsString += "-";
        }
      }
      try {
        setLoading(true);

        if (data?.data.orderID) {
          var flow_config = {
            merchantId: data.data.links[1].parameters.mercid,

            bdOrderId: data.data.links[1].parameters.bdorderid,

            authToken: data.data.links[1].headers.authorization,
            childWindow: true,

            returnUrl: `https://nfobooks.checkout.nationalfinanceolympiad.com/CheckoutPage/?order_id=${data.data.orderID}&item_ids=${itemIdsString}`,
            retryCount: 3,
            prefs: {
              payment_categories: ["card", "nb", "upi"],
            },
            netBanking: {
              showPopularBanks: "Y",
              popularBanks: ["Kotak Bank", " AXIS Bank [Retail]"],
            },
          };

          var responseHandler = function (txn: {
            status: string;
            response: string;
          }) {
            console.log("callback received status:: ", txn.status);
            Sentry.captureMessage(
              `callback received status:${txn.status}`,
              "info"
            );
            console.log("callback received response:: ", txn.response);
            Sentry.captureMessage(
              `callback received response:${txn.response}`,
              "info"
            );
          };

          var theme_config = {
            sdkPrimaryColor: "#023b8f",
            sdkAccentColor: "#023b8f",
            sdkBackgroundColor: "#FFF",
            sdkBannerColor: "#FFF",
          };

          var config = {
            responseHandler: responseHandler,
            merchantLogo: merchantLogoString,
            flowConfig: flow_config,
            flowType: "payments",
            themeConfig: theme_config,
          };
          //@ts-ignore
          if (window.loadBillDeskSdk) {
            //@ts-ignore
            window.loadBillDeskSdk(config);
          }
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        console.log(err);
        throw new Error(`BillDesk Error:${err}`);
      }
    }
  };

  useEffect(() => {
    const keys: (keyof UserDetails)[] = [
      "email",
      "mobileNumber",
      "address",
      "flatNo",
      "city",
      "state",
      "pincode",
      "paymentType",
      "firstName",
      "lastName",
    ];

    const storedUserDetails = keys.reduce((details, key) => {
      const value = sessionStorage.getItem(key);
      if (value) {
        if (key === "paymentType") {
          if (value === "prepaid" || value === "cod") {
            details[key] = value;
          }
        } else if (key === "grade") {
          details[key] = parseInt(value) || 0;
        } else {
          details[key] = value;
        }
      }
      return details;
    }, {} as { [K in keyof UserDetails]: UserDetails[K] });

    setUserDetails((prevDetails) => ({
      ...prevDetails,
      ...storedUserDetails,
    }));
  }, []);

  return (
    <div className="checkout">
      {verifyPaymentProgress || loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#FFF",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div>
          <UniversalCheckoutHeader />
          <CartSubtitle
            stateCheckoutItems={checkOutItems}
            stateDefaultItems={defaultItems}
            stateRecommendedItems={recommendedItems}
            cbRemoveProduct={handleRemoveItem}
            cbAddRecommended={handleAddItemFromRecommended}
            cbHandlePaymentClick={handlePaymentClickNFO}
            couponState={couponState}
            couponInput={couponInput}
            setCouponInput={setCouponInput}
            handleApplyCoupon={handleApplyCoupon}
            handleRemoveCoupon={handleRemoveCoupon}
            isCouponEnabled={isCouponEnabled}
          />
          <CheckoutPaymentFooter />
          <CheckoutFooter />
        </div>
      )}
      <PaymentFailedModal
        paymentFailed={paymentFailed}
        setPaymentFailed={setPaymentFailed}
      />
    </div>
  );
};

export default CheckoutPage;
